import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { purgeMexLocalCache } from "../lib/activeAlap";

const AuthContext = createContext(null);

// Session-scoped flag: amíg igaz, az admin a felület szempontjából
// tester-ként látja a kártyákat. Nem írja át a role-t adatbázisban,
// nem jelentkezik ki — pusztán a UI rendereléshez használt jelző.
const PREVIEW_KEY = "mex.previewAsTester";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewAsTester, setPreviewAsTesterState] = useState(() => {
    try { return sessionStorage.getItem(PREVIEW_KEY) === "1"; } catch { return false; }
  });

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      const nextUser = res.data;
      setUser(nextUser);
      // Détect user swap on this device: if the previous session was signed
      // in as a different user, purge every MEX-scoped cache so the new
      // account never inherits the previous one's active alap/drafts/etc.
      try {
        const LAST_KEY = "mex.last_user_id";
        const prev = localStorage.getItem(LAST_KEY);
        const nowId = nextUser?.user_id || null;
        if (nowId && prev && prev !== nowId) {
          purgeMexLocalCache();
        }
        if (nowId) localStorage.setItem(LAST_KEY, nowId);
      } catch { /* ignore */ }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash && window.location.hash.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setUser(null);
    // Purge every MEX-scoped local/session key so a subsequent login on
    // this device (possibly a different account) cannot inherit any stale
    // active alap, drafts, preview flags, or agent context.
    purgeMexLocalCache();
    setPreviewAsTesterState(false);
    window.location.href = "/";
  };

  // Tester preview = TISZTÁN frontend UI flag, ami csak az
  // effectiveRole-t váltja át "tester"-re. NEM nyúl a `mex_active_alap`
  // localStorage-hoz, NEM cseréli ki Tamás backend session-jét, NEM ír
  // bele semmilyen user collection-be. Korábbi snapshot/restore logika
  // törölve (instabilitást okozott). A preview módban végzett bármilyen
  // action továbbra is Tamás saját admin user_id-ja alá kerül — ez a
  // backend-szintű izoláció már önmagában garantálja, hogy más user
  // workspace-éhez NEM nyúl.
  const setPreviewAsTester = (next) => {
    const v = !!next;
    try {
      if (v) sessionStorage.setItem(PREVIEW_KEY, "1");
      else sessionStorage.removeItem(PREVIEW_KEY);
    } catch { /* swallow */ }
    setPreviewAsTesterState(v);
  };

  // Csak admin user kapcsolhatja be (biztosíték: ha valamiért a flag
  // bent ragad egy tester sessionben, soha ne aktivál preview UI-t).
  const isReallyAdmin = user?.role === "admin";
  const effectivePreview = isReallyAdmin && previewAsTester;
  const effectiveRole = effectivePreview ? "tester" : (user?.role || null);

  return (
    <AuthContext.Provider value={{
      user,
      setUser,
      loading,
      logout,
      checkAuth,
      // Preview API
      previewAsTester: effectivePreview,
      setPreviewAsTester,
      isReallyAdmin,        // mindig a DB-beli role
      effectiveRole,        // amit a UI-nak látnia kell ('admin' | 'tester')
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
